// frontend/src/pages/OrganizationManagement.js
import React, { useEffect, useMemo, useState, useContext } from "react";
import Navbar from "../components/Navbar";
import { AuthContext } from "../contexts/AuthContext";
import {
  apiFetch,
  fetchOrganizations,
  fetchBillingTransactions,
  fetchSubscriptionTransactions,
} from "../services/api";

export default function OrganizationManagement() {
  const { user, token: ctxToken } = useContext(AuthContext);
  const isSuper =
    user?.group?.toLowerCase?.() === "superadmin" || user?.user_group_name === "superadmin";

  const [list, setList] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [logoFile, setLogoFile] = useState(null);

  const blankForm = {
    name: "",
    org_domain: "",
    bt_id: "",
    allow_extra_interviews: 0,
    status: 1, // <- use ORGANIZATION.status
    subscription_start: "",
    subscription_end: "",
    logo_url: "",
  };
  const [form, setForm] = useState(blankForm);

  // ---------- helpers ----------
  const normalizeTiers = (payload) => {
    let arr = [];
    if (Array.isArray(payload)) arr = payload;
    else if (Array.isArray(payload?.data)) arr = payload.data;
    else if (Array.isArray(payload?.tiers)) arr = payload.tiers;
    else if (Array.isArray(payload?.rows)) arr = payload.rows;

    return arr
      .map((t) => ({
        bt_id: Number(t.bt_id ?? t.id ?? t.BT_ID ?? NaN),
        bt_name: String(t.bt_name ?? t.name ?? t.BT_NAME ?? ""),
      }))
      .filter((t) => Number.isFinite(t.bt_id) && t.bt_name);
  };

  const tierNameById = useMemo(() => {
    const map = {};
    tiers.forEach((t) => (map[t.bt_id] = t.bt_name));
    return map;
  }, [tiers]);

  const visibleList = useMemo(
    () => (list || []).filter((o) => (o.id ?? o.organization_id) !== 1),
    [list]
  );

  // ---------- fetchers ----------
  async function loadOrgs() {
    setLoading(true);
    try {
      const data = await fetchOrganizations();
      setList(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("organizations.fetch error", e);
      setList([]);
    } finally {
      setLoading(false);
    }
  }

  async function loadTiers() {
    const API_BASE =
      process.env.REACT_APP_API_BASE_URL ||
      process.env.REACT_APP_BACKEND_URL ||
      "/api";

    const bearer =
      ctxToken ||
      localStorage.getItem("token") ||
      sessionStorage.getItem("token") ||
      "";

    const common = {
      headers: {
        Authorization: bearer ? `Bearer ${bearer}` : "",
        Accept: "application/json",
      },
      credentials: "same-origin",
    };

    // Primary endpoint
    try {
      const r = await fetch(`${API_BASE}/billing/tiers`, common);
      if (r.ok) {
        const json = await r.json();
        const norm = normalizeTiers(json);
        if (norm.length) {
          setTiers(norm);
          return;
        }
      }
    } catch (_) {}

    // Fallback endpoint
    try {
      const r = await fetch(`${API_BASE}/organizations/billing-tiers`, common);
      if (r.ok) {
        const json = await r.json();
        const norm = normalizeTiers(json);
        setTiers(norm);
        return;
      }
    } catch (_) {}

    console.error("tiers.fetch error: both endpoints returned no data.");
    setTiers([]);
  }

  useEffect(() => {
    loadTiers().finally(loadOrgs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- CRUD ----------
  function startNew() {
    setEditingId(null);
    setLogoFile(null);
    setForm(blankForm);
  }

  function startEdit(org) {
    setEditingId(org.id ?? org.organization_id);
    setLogoFile(null);

    setForm({
      name: org.name ?? org.organization_name ?? "",
      org_domain: org.org_domain ?? "",
      bt_id: org.bt_id ?? "",
      allow_extra_interviews: Number(org.allow_extra_interviews ?? 0),
      status: Number(org.status ?? 1), // <- read ORGANIZATION.status
      subscription_start: (org.subscription_start || "").replace(" ", "T").slice(0, 16),
      subscription_end: (org.subscription_end || "").replace(" ", "T").slice(0, 16),
      logo_url: org.logo_url ?? "",
    });
  }

  function onChange(e) {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setForm((f) => ({ ...f, [name]: checked ? 1 : 0 }));
    } else {
      setForm((f) => ({ ...f, [name]: value }));
    }
  }

  async function remove(id) {
    if (!isSuper) return;
    if (!window.confirm("Delete this organization?")) return;
    try {
      await apiFetch(`organizations/${id}`, { method: "DELETE" });
      setList((prev) => prev.filter((o) => (o.id ?? o.organization_id) !== id));
      if (editingId === id) startNew();
    } catch (e) {
      console.error("organizations.delete error", e);
      alert("Failed to delete organization.");
    }
  }

  // Use FormData (no explicit Content-Type) so file uploads work
  async function handleSubmit(e) {
    e.preventDefault();

    const API_BASE =
      process.env.REACT_APP_API_BASE_URL ||
      process.env.REACT_APP_BACKEND_URL ||
      "/api";

    const bearer =
      ctxToken ||
      localStorage.getItem("token") ||
      sessionStorage.getItem("token") ||
      "";

    const normDT = (val) => {
      if (!val) return "";
      const [d, t] = val.split("T");
      return `${d} ${t}:00`;
    };

    const fd = new FormData();
    fd.append("name", form.name.trim());
    fd.append("org_domain", form.org_domain.trim());
    fd.append("bt_id", form.bt_id === "" ? "" : String(form.bt_id));
    fd.append("allow_extra_interviews", String(form.allow_extra_interviews ?? 0));
    fd.append("status", String(form.status ?? 1)); // <- submit ORGANIZATION.status
    fd.append("subscription_start", normDT(form.subscription_start));
    fd.append("subscription_end", normDT(form.subscription_end));
    if (logoFile) fd.append("logo", logoFile);

    const endpoint = editingId
      ? `${API_BASE}/organizations/${editingId}`
      : `${API_BASE}/organizations`;
    const method = editingId ? "PUT" : "POST";

    try {
      const res = await fetch(endpoint, {
        method,
        headers: {
          Authorization: bearer ? `Bearer ${bearer}` : "",
          Accept: "application/json",
        },
        body: fd,
      });

      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || `HTTP ${res.status}`);
      }

      await Promise.all([loadTiers(), loadOrgs()]);
      startNew();
      alert("Organization saved.");
    } catch (e) {
      console.error("organizations.save error", e.message || e);
      alert("Failed to save organization.");
    }
  }

  // ---------- Transactions (unchanged) ----------
  const [txVisible, setTxVisible] = useState(false);
  const [txOrgId, setTxOrgId] = useState(null);
  const [billingTx, setBillingTx] = useState([]);
  const [subsTx, setSubsTx] = useState([]);

  async function openTransactions(orgId) {
    setTxVisible(true);
    setTxOrgId(orgId);
    try {
      const [b, s] = await Promise.all([
        fetchBillingTransactions(orgId),
        fetchSubscriptionTransactions(orgId),
      ]);
      setBillingTx(Array.isArray(b) ? b : []);
      setSubsTx(Array.isArray(s) ? s : []);
    } catch (e) {
      console.error("transactions.fetch error", e);
      setBillingTx([]);
      setSubsTx([]);
    }
  }

  // ---------- Render ----------
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="p-4 max-w-7xl mx-auto">
        <h2 className="text-2xl font-bold mb-4">Organizations</h2>

        {/* FORM */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <h3 className="text-lg font-semibold mb-3">
            {editingId ? "Edit Organization" : "New Organization"}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name</label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  required
                  className="w-full rounded border border-gray-300 p-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Domain</label>
                <input
                  type="text"
                  name="org_domain"
                  value={form.org_domain}
                  onChange={onChange}
                  className="w-full rounded border border-gray-300 p-2"
                />
              </div>

              {/* Billing Tier dropdown */}
              <div>
                <label className="block text-sm font-medium mb-1">Billing Tier</label>
                <select
                  name="bt_id"
                  value={form.bt_id}
                  onChange={onChange}
                  className="w-full rounded border border-gray-300 p-2 bg-white"
                >
                  <option value="">Select Billing Tier</option>
                  {tiers.map((t) => (
                    <option key={t.bt_id} value={t.bt_id}>
                      {t.bt_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Allow Extra Interviews */}
              <div className="flex items-center gap-3 mt-6">
                <input
                  id="allow_extra_interviews"
                  type="checkbox"
                  name="allow_extra_interviews"
                  checked={Number(form.allow_extra_interviews) === 1}
                  onChange={onChange}
                  className="h-4 w-4"
                />
                <label htmlFor="allow_extra_interviews" className="text-sm font-medium">
                  Allow Extra Interviews
                </label>
              </div>

              {/* Active (uses ORGANIZATION.status) */}
              <div className="flex items-center gap-3 mt-6">
                <input
                  id="status"
                  type="checkbox"
                  name="status"
                  checked={Number(form.status) === 1}
                  onChange={onChange}
                  className="h-4 w-4"
                />
                <label htmlFor="status" className="text-sm font-medium">
                  Active
                </label>
              </div>

              {/* Subscription Start */}
              <div>
                <label className="block text-sm font-medium mb-1">Subscription Starts</label>
                <input
                  type="datetime-local"
                  name="subscription_start"
                  value={form.subscription_start}
                  onChange={onChange}
                  className="w-full rounded border border-gray-300 p-2"
                />
              </div>

              {/* Subscription End */}
              <div>
                <label className="block text-sm font-medium mb-1">Subscription Ends</label>
                <input
                  type="datetime-local"
                  name="subscription_end"
                  value={form.subscription_end}
                  onChange={onChange}
                  className="w-full rounded border border-gray-300 p-2"
                />
              </div>

              {/* Logo upload */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Organization Logo</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setLogoFile(e.target.files?.[0] ?? null)}
                  className="w-full rounded border border-gray-300 p-2 bg-white"
                />
                {form.logo_url ? (
                  <p className="text-xs text-gray-500 mt-1">
                    Current: <span className="underline">{form.logo_url}</span>
                  </p>
                ) : null}
              </div>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
              >
                {editingId ? "Save Changes" : "Create Organization"}
              </button>
              {editingId && (
                <button
                  type="button"
                  onClick={startNew}
                  className="ml-2 px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        {/* LIST */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold">Organization List</h3>
            <div className="flex gap-2">
              <button
                className="px-3 py-2 rounded bg-green-600 text-white hover:bg-green-700"
                onClick={startNew}
              >
                New Organization
              </button>
            </div>
          </div>

          {loading ? (
            <div className="p-3 text-gray-600">Loading…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-200 rounded">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="text-left p-2 border-b">ID</th>
                    <th className="text-left p-2 border-b">Name</th>
                    <th className="text-left p-2 border-b">Domain</th>
                    <th className="text-left p-2 border-b">Billing Tier</th>
                    <th className="text-left p-2 border-b">Active</th>
                    <th className="text-left p-2 border-b">Allow Extra Interviews</th>
                    <th className="text-left p-2 border-b">Subscription Starts</th>
                    <th className="text-left p-2 border-b">Subscription Ends</th>
                    <th className="text-left p-2 border-b">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleList.map((o) => {
                    const id = o.id ?? o.organization_id;
                    const btLabelFromJoin = o.bt_name;
                    const btLabelFromMap =
                      tierNameById[Number(o.bt_id)] ??
                      (typeof o.bt_id === "number" ? `Tier #${o.bt_id}` : "");

                    const statusVal = Number(o.status ?? 0); // <- use ORGANIZATION.status for display

                    return (
                      <tr key={id} className="hover:bg-gray-50">
                        <td className="p-2 border-b">{id}</td>
                        <td className="p-2 border-b">{o.name ?? o.organization_name}</td>
                        <td className="p-2 border-b">{o.org_domain}</td>
                        <td className="p-2 border-b">{btLabelFromJoin || btLabelFromMap || "—"}</td>
                        <td className="p-2 border-b">{statusVal === 1 ? "Yes" : "No"}</td>
                        <td className="p-2 border-b">
                          {Number(o.allow_extra_interviews) === 1 ? "Yes" : "No"}
                        </td>
                        <td className="p-2 border-b">
                          {(o.subscription_start || "").replace("T", " ")}
                        </td>
                        <td className="p-2 border-b">
                          {(o.subscription_end || "").replace("T", " ")}
                        </td>
                        <td className="p-2 border-b">
                          <button
                            className="px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
                            onClick={() => startEdit(o)}
                          >
                            Edit
                          </button>
                          {isSuper && (
                            <>
                              <button
                                className="ml-2 px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700"
                                onClick={() => remove(id)}
                              >
                                Delete
                              </button>
                              <button
                                className="ml-2 px-2 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700"
                                onClick={() => openTransactions(id)}
                              >
                                Transactions
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {visibleList.length === 0 && (
                    <tr>
                      <td className="p-3 text-gray-500" colSpan={9}>
                        No organizations to show.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* TRANSACTIONS */}
        {txVisible && (
          <div className="bg-white rounded-lg shadow p-4 mt-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Transactions (Org #{txOrgId})</h3>
              <button
                className="px-3 py-2 rounded bg-gray-200 hover:bg-gray-300"
                onClick={() => setTxVisible(false)}
              >
                Close
              </button>
            </div>

            <div className="mt-4">
              <h4 className="font-semibold mb-2">Billing</h4>
              <table className="min-w-full border border-gray-200 rounded mb-4">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="text-left p-2 border-b">ID</th>
                    <th className="text-left p-2 border-b">Amount</th>
                    <th className="text-left p-2 border-b">Date</th>
                    <th className="text-left p-2 border-b">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {billingTx.map((b) => (
                    <tr key={b.id} className="hover:bg-gray-50">
                      <td className="p-2 border-b">{b.id}</td>
                      <td className="p-2 border-b">{b.amount}</td>
                      <td className="p-2 border-b">{(b.created_at || "").replace("T", " ")}</td>
                      <td className="p-2 border-b">{b.notes}</td>
                    </tr>
                  ))}
                  {billingTx.length === 0 && (
                    <tr>
                      <td className="p-3 text-gray-500" colSpan={4}>
                        No billing transactions
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              <h4 className="font-semibold mb-2">Subscription</h4>
              <table className="min-w-full border border-gray-200 rounded">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="text-left p-2 border-b">ID</th>
                    <th className="text-left p-2 border-b">Action</th>
                    <th className="text-left p-2 border-b">Date</th>
                    <th className="text-left p-2 border-b">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {subsTx.map((s) => (
                    <tr key={s.id} className="hover:bg-gray-50">
                      <td className="p-2 border-b">{s.id}</td>
                      <td className="p-2 border-b">{s.action}</td>
                      <td className="p-2 border-b">{(s.created_at || "").replace("T", " ")}</td>
                      <td className="p-2 border-b">{s.notes}</td>
                    </tr>
                  ))}
                  {subsTx.length === 0 && (
                    <tr>
                      <td className="p-3 text-gray-500" colSpan={4}>
                        No subscription transactions
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
